import { useEffect, useMemo, useRef, useState } from "react";
import { buildScheduleNotifications, defaultNotificationSettings } from "../utils/scheduleNotifications";

function readSettings(key) {
  try {
    const saved = JSON.parse(localStorage.getItem(key));
    if (saved === true) return { ...defaultNotificationSettings, enabled: true };
    return { ...defaultNotificationSettings, ...(saved && typeof saved === "object" ? saved : {}) };
  } catch { return { ...defaultNotificationSettings }; }
}
function readSeen(key) {
  try {
    const saved = JSON.parse(localStorage.getItem(key));
    return Array.isArray(saved) ? saved.filter((id) => typeof id === "string") : [];
  } catch { return []; }
}

function readMessages(scope) {
  try {
    const saved = JSON.parse(localStorage.getItem(`${scope}:messages`));
    return Array.isArray(saved) ? saved.filter((message) => message &&
      [message.id, message.name, message.title, message.text].every((value) => typeof value === "string")) : [];
  } catch { return []; }
}

function readCrossings(scope) {
  try {
    const saved = JSON.parse(localStorage.getItem(`${scope}:crossings`));
    return saved && typeof saved === "object" && !Array.isArray(saved) ? saved : {};
  } catch { return {}; }
}

const crossingId = (id, crossings) => crossings[id]?.count > 1 ? `${id}:crossing:${crossings[id].count}` : id;

export default function useScheduleNotifications({ companyUid, userId, schemaId, employees, shifts, workTimes, currentDate, isOpen, dataReady = true }) {
  const settingsKey = `schedule-notifications:${companyUid}:${userId}`;
  const scope = `${settingsKey}:${schemaId}:${currentDate.getFullYear()}-${currentDate.getMonth() + 1}`;
  const [saved, setSaved] = useState(() => ({ key: settingsKey, settings: readSettings(settingsKey) }));
  const [read, setRead] = useState(() => ({ scope, ids: readSeen(scope) }));
  const [dismissed, setDismissed] = useState(() => ({ scope, ids: readSeen(`${scope}:dismissed`) }));
  const [crossings, setCrossings] = useState(() => ({ scope, values: readCrossings(scope) }));
  const [history, setHistory] = useState(() => ({ scope, messages: readMessages(scope) }));
  const [error, setError] = useState("");
  const [activeToast, setActiveToast] = useState(null);
  const delivered = useRef(new Set());
  const wasOpen = useRef(isOpen);
  // Resolve a new account/period immediately, before effects, to avoid stale notifications.
  const settings = useMemo(() => saved.key === settingsKey ? saved.settings : readSettings(settingsKey), [saved, settingsKey]);
  const seenIds = useMemo(() => read.scope === scope ? read.ids : readSeen(scope), [read, scope]);
  const dismissedIds = useMemo(() => dismissed.scope === scope ? dismissed.ids : readSeen(`${scope}:dismissed`), [dismissed, scope]);
  const storedMessages = useMemo(() => history.scope === scope ? history.messages : readMessages(scope), [history, scope]);
  const storedCrossings = useMemo(() => crossings.scope === scope ? crossings.values : readCrossings(scope), [crossings, scope]);
  // Track threshold transitions independently of which notification switches are enabled.
  const nextCrossings = useMemo(() => {
    if (!dataReady) return storedCrossings;
    const activeIds = new Set(buildScheduleNotifications({
      settings: { ...settings, enabled: true, employment: true, overtime: true, overtimeLimit: true, dailyRest: true },
      employees, shifts, workTimes, currentDate,
    }).map((message) => message.id));
    const next = { ...storedCrossings };
    Object.keys(next).forEach((id) => {
      if (!activeIds.has(id)) next[id] = { ...next[id], active: false };
    });
    activeIds.forEach((id) => {
      const previous = storedCrossings[id];
      next[id] = { active: true, count: (previous?.count || 0) + (previous?.active ? 0 : 1) };
    });
    return next;
  }, [dataReady, storedCrossings, settings, employees, shifts, workTimes, currentDate]);
  useEffect(() => {
    if (JSON.stringify(storedCrossings) === JSON.stringify(nextCrossings)) return;
    setCrossings({ scope, values: nextCrossings });
    try { localStorage.setItem(`${scope}:crossings`, JSON.stringify(nextCrossings)); }
    catch { setError("Notisernas gränspassager kunde inte sparas i webbläsaren."); }
  }, [scope, storedCrossings, nextCrossings]);
  const messages = useMemo(() => {
    const retained = new Map(storedMessages.map((message) => [message.id, message]));
    if (dataReady) buildScheduleNotifications({ settings, employees, shifts, workTimes, currentDate }).forEach((message) => {
      const id = crossingId(message.id, nextCrossings);
      if (!retained.has(id)) retained.set(id, { ...message, id });
    });
    return [...retained.values()].filter((message) => !dismissedIds.includes(message.id));
  }, [storedMessages, settings, employees, shifts, workTimes, currentDate, dismissedIds, nextCrossings, dataReady]);
  useEffect(() => {
    if (JSON.stringify(storedMessages) === JSON.stringify(messages)) return;
    setHistory({ scope, messages });
    try { localStorage.setItem(`${scope}:messages`, JSON.stringify(messages)); }
    catch { setError("Meddelandena kunde inte sparas i webbläsaren."); }
  }, [scope, storedMessages, messages]);
  const unread = useMemo(() => messages.filter((message) => !seenIds.includes(message.id)), [messages, seenIds]);
  useEffect(() => {
    if (isOpen && !wasOpen.current) setActiveToast(null);
    wasOpen.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    const deliveredKey = `${scope}:delivered`;
    const deliveredIds = new Set(readSeen(deliveredKey));
    const fresh = unread.filter((message) => !deliveredIds.has(message.id) &&
      !delivered.current.has(`${scope}:${message.id}`));
    if (!fresh.length) return;
    fresh.forEach((message) => {
      delivered.current.add(`${scope}:${message.id}`);
      deliveredIds.add(message.id);
    });
    try { localStorage.setItem(deliveredKey, JSON.stringify([...deliveredIds])); }
    catch { setError("Visningsstatus för notiser kunde inte sparas i webbläsaren."); }
    setActiveToast({ scope, message: fresh[0] });
  }, [unread, scope]);

  useEffect(() => {
    if (!isOpen || !unread.length) return;
    const ids = [...new Set([...seenIds, ...unread.map((message) => message.id)])];
    setRead({ scope, ids });
    try { localStorage.setItem(scope, JSON.stringify(ids)); }
    catch { setError("Lässtatus kunde inte sparas i webbläsaren."); }
  }, [isOpen, unread, scope, seenIds]);

  useEffect(() => {
    if (!activeToast) return;
    const timer = window.setTimeout(() => setActiveToast(null), 7000);
    return () => window.clearTimeout(timer);
  }, [activeToast]);

  const saveSettings = (next) => {
    try {
      localStorage.setItem(settingsKey, JSON.stringify(next));
      setSaved({ key: settingsKey, settings: { ...next } });
      setError("");
      const matching = (dataReady ? buildScheduleNotifications({ settings: next, employees, shifts, workTimes, currentDate }) : [])
        .map((message) => ({ ...message, id: crossingId(message.id, nextCrossings) }))
        .filter((message) => !dismissedIds.includes(message.id));
      setActiveToast(matching.length ? { scope, message: matching[0] } : null);
      return true;
    } catch {
      setError("Inställningarna kunde inte sparas i webbläsaren. Försök igen.");
      return false;
    }
  };
  const clearMessages = (messageIds) => {
    const ids = [...new Set([...dismissedIds, ...messageIds])];
    try {
      localStorage.setItem(`${scope}:dismissed`, JSON.stringify(ids));
      setDismissed({ scope, ids });
      setActiveToast((toast) => toast?.scope === scope && messageIds.includes(toast.message.id) ? null : toast);
      setError("");
    } catch {
      setError("Notiserna kunde inte rensas i webbläsaren. Försök igen.");
    }
  };
  const clearAll = () => clearMessages(messages.map((message) => message.id));
  const clearMessage = (id) => clearMessages([id]);
  return { settings, messages, saveSettings, clearAll, clearMessage, error, unreadCount: isOpen ? 0 : unread.length,
    toast: settings.enabled && activeToast?.scope === scope && messages.some((message) => message.id === activeToast.message.id)
      ? activeToast.message : null,
    dismissToast: () => setActiveToast(null), settingsKey };
}
